/**
 * `FilterBar` - exact-match filter for `ext_account_id` or `legacy_id`.
 *
 * Mutually exclusive: typing in one input disables the other (the
 * upstream returns 400 `conflicting_filters` if both are sent; we enforce
 * the constraint client-side so the user never trips that). The inactive
 * input carries a tooltip explaining why it is locked.
 *
 * Submit on Enter or via the explicit "Apply" button. Apply writes both
 * values into the URL query string; Clear resets both. URL is the
 * authoritative state - the component does not own a fetch trigger of
 * its own.
 *
 * Accessibility:
 *   - `role="search"` on the form wrapper.
 *   - `<label htmlFor>` paired with each input.
 *   - `aria-describedby` on the inactive input points at the tooltip text
 *     so screen readers announce why the field is disabled.
 *   - `legacy_id` input is `inputMode="numeric"` so mobile keyboards
 *     surface the numeric pad; we keep `type="text"` so non-numeric
 *     input is held as a user error rather than silently dropped.
 *   - Both inputs outline in `border-input` (3.62:1 on white), not
 *     `border-default` (1.47:1). The outline is the only thing marking
 *     where these fields start and end, which makes it a UI component
 *     boundary under WCAG 1.4.11, not decoration. The card edge around
 *     the form stays `border-default` - that one is decorative. MUR Q15.
 */

import { useEffect, useId, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'

import { Button } from '../../components/button'
import { isPositiveInteger } from './url-state'

export interface FilterBarProps {
  readonly initialExtAccountId: string | null
  readonly initialLegacyId: string | null
  readonly onApply: (next: { extAccountId: string | null; legacyId: string | null }) => void
  readonly onClear: () => void
  /** Forwarded so other components (Pagination) can render alongside. */
  readonly trailing?: React.ReactNode
}

export interface FilterBarHandle {
  focus(): void
}

export function FilterBar({
  initialExtAccountId,
  initialLegacyId,
  onApply,
  onClear,
  trailing,
}: FilterBarProps): JSX.Element {
  const [ext, setExt] = useState<string>(initialExtAccountId ?? '')
  const [legacy, setLegacy] = useState<string>(initialLegacyId ?? '')
  const [legacyError, setLegacyError] = useState<string | null>(null)
  const extInputRef = useRef<HTMLInputElement | null>(null)

  // When the URL changes (e.g. Clear via empty-state button), sync the
  // local state so the inputs reflect the authoritative URL.
  useEffect(() => {
    setExt(initialExtAccountId ?? '')
    setLegacy(initialLegacyId ?? '')
  }, [initialExtAccountId, initialLegacyId])

  // Expose focus() on the bar so the page can refocus it after Esc on a
  // row, and so the empty-state "Try a search" copy points somewhere
  // actionable.
  useEffect(() => {
    if (initialExtAccountId === null && initialLegacyId === null) {
      // Defer to next paint so the input is mounted before we focus it.
      const id = window.setTimeout(() => extInputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
    return undefined
  }, [initialExtAccountId, initialLegacyId])

  const extDisabled = legacy.trim() !== ''
  const legacyDisabled = ext.trim() !== ''

  const extDescId = useId()
  const legacyDescId = useId()

  const submit = (): void => {
    const trimmedExt = ext.trim()
    const trimmedLegacy = legacy.trim()

    if (trimmedLegacy !== '' && !isPositiveInteger(trimmedLegacy)) {
      setLegacyError('Legacy id must be a positive integer.')
      return
    }
    setLegacyError(null)

    if (trimmedExt !== '') {
      onApply({ extAccountId: trimmedExt, legacyId: null })
      return
    }
    if (trimmedLegacy !== '') {
      onApply({ extAccountId: null, legacyId: trimmedLegacy })
      return
    }
    onApply({ extAccountId: null, legacyId: null })
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    submit()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submit()
    }
  }

  const handleClear = (): void => {
    setExt('')
    setLegacy('')
    setLegacyError(null)
    onClear()
  }

  return (
    <form
      role="search"
      aria-label="Filter members"
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-murmuration-4 rounded-md border border-border-default bg-bg-surface px-murmuration-4 py-murmuration-3 shadow-elev-1"
    >
      <div className="flex flex-col">
        <label
          htmlFor="filter-ext-account-id"
          className="text-murmuration-label text-text-secondary"
        >
          Account number
        </label>
        <input
          id="filter-ext-account-id"
          ref={extInputRef}
          type="text"
          value={ext}
          onChange={(e) => setExt(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={extDisabled}
          aria-describedby={extDisabled ? extDescId : undefined}
          maxLength={32}
          autoComplete="off"
          spellCheck={false}
          className="mt-murmuration-1 w-56 rounded-sm border border-border-input bg-bg-surface px-murmuration-2 py-murmuration-1 font-mono text-murmuration-code text-text-primary disabled:cursor-not-allowed disabled:bg-bg-surface-2 disabled:text-text-muted"
        />
        {extDisabled && (
          <span id={extDescId} className="sr-only">
            Disabled while a legacy id filter is set. Clear the legacy id to search by account
            number.
          </span>
        )}
      </div>

      <div className="flex flex-col">
        <label htmlFor="filter-legacy-id" className="text-murmuration-label text-text-secondary">
          Legacy id
        </label>
        <input
          id="filter-legacy-id"
          type="text"
          inputMode="numeric"
          value={legacy}
          onChange={(e) => setLegacy(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={legacyDisabled}
          aria-describedby={legacyDisabled ? legacyDescId : undefined}
          aria-invalid={legacyError !== null}
          maxLength={20}
          autoComplete="off"
          spellCheck={false}
          className="mt-murmuration-1 w-44 rounded-sm border border-border-input bg-bg-surface px-murmuration-2 py-murmuration-1 font-mono text-murmuration-code text-text-primary disabled:cursor-not-allowed disabled:bg-bg-surface-2 disabled:text-text-muted"
        />
        {legacyDisabled && (
          <span id={legacyDescId} className="sr-only">
            Disabled while an account number filter is set. Clear the account number to search by
            legacy id.
          </span>
        )}
        {legacyError !== null && (
          <span role="alert" className="mt-murmuration-1 text-murmuration-label text-status-danger">
            {legacyError}
          </span>
        )}
      </div>

      <div className="flex items-center gap-murmuration-2">
        <Button type="submit" variant="primary">
          Apply
        </Button>
        <Button onClick={handleClear}>Clear</Button>
      </div>

      {trailing && <div className="ml-auto flex items-center">{trailing}</div>}
    </form>
  )
}
